import {
  CareCoordinationStatus,
  CareModel,
  ConditionCategory,
  RiskLevel,
} from '../../models/user';

// values equal to Users.UserType backend Enum
export const USER_TYPES = {
  internalUser: "Internal user",
  trueUser: "True user",
  freeTrialDidNotConvert: "Free trial did not convert",
  betaUser: "Beta user",
  freeUser: "Free user",
  paymentFailed: "Payment Failed",
};

// Digital-only is deliberately excluded — team feedback (Aug 2026) asked for
// it off the filter, not off the CareModel enum. Patients with that care
// model still exist and still render correctly elsewhere; they just aren't
// selectable from this dropdown.
export const CARE_MODEL_OPTIONS = [
  { label: CareModel.Collaborative, value: CareModel.Collaborative },
  { label: CareModel.CoachOnly, value: CareModel.CoachOnly },
];

export const DEFAULT_AVATAR_IMG_URL = 'https://ayble-uploads-production.s3.amazonaws.com/avatars/coaches/default.png'

// --- Roster filter sidebar (CareOS Filters Redesign) ----------------------
// Option lists for the filter sidebar. Ordered as the brief lists them, which
// is also funnel order for care coordination — the sidebar renders them in
// sequence, so the array order is the on-screen order.

export type FilterOption<T extends string> = { value: T; label: string };

/**
 * Display label for every care-coordination status.
 *
 * Spelled out as an explicit `Record` over the whole union rather than derived
 * from the filter's option list, and deliberately so. `CareCoordinationPill`
 * renders the roster's Care Coordination column through this map for *every*
 * member — including the `cancelled` ones the sidebar no longer offers as a
 * funnel filter (round 6 moved Cancelled to Enrollment Status). While this map
 * was built by reducing over the filter options, shortening that list would
 * have silently dropped a key and rendered `undefined` in the column, and the
 * `as Record<...>` cast on the reduce hid that from tsc. Declared this way, a
 * status added to `CareCoordinationStatus` is a type error here until someone
 * gives it a label.
 *
 * `ongoing_care`'s label is deliberately not a restatement of its value.
 * Once a member is established in care, "they're in ongoing care" is the
 * least useful thing the cell could say — every row in that stage says it.
 * What a care team is actually scanning for is how long it's been since the
 * member did anything, so the tag leads with "Last Engaged" and carries the
 * date (see `CareCoordinationPill`). The value stays `ongoing_care` because
 * that's still the funnel position it encodes.
 */
export const CARE_COORDINATION_LABELS: Record<CareCoordinationStatus, string> = {
  never_logged_in: 'Never Logged In',
  onboarding: 'Onboarding',
  baseline_complete: 'Baseline Complete',
  booked_initial_visit: 'Initial Visit Scheduled',
  initial_visit_occurred: 'Initial Visit Occurred',
  ongoing_care: 'Last Engaged',
  cancelled: 'Cancelled',
};

/**
 * The funnel stages the Care Coordination filter offers, in funnel order.
 *
 * A strict subset of `CareCoordinationStatus`, and shorter than
 * `CARE_COORDINATION_LABELS` on purpose — these are the stages you can
 * *filter* by, not the ones a member can *be* in:
 *  - `cancelled` moved to Enrollment Status (round 6). A cancelled member has
 *    left the funnel rather than occupying a stage in it, so offering it here
 *    alongside the live stages put two different kinds of thing in one group.
 *  - "New" was never a funnel stage at all — it's signup recency, which
 *    co-occurs with any stage. See `ENROLLMENT_STATUS_OPTIONS`.
 *  - `ongoing_care` ("Last Engaged") was cut in round 7 at the team's request.
 *    Its label answers a recency question rather than naming a funnel
 *    position, and recency now has its own roster column — so what's left here
 *    is exactly the pre-ongoing pipeline, which is what this group is for.
 *
 * All three still render in the roster wherever the label map is used; they
 * just aren't selectable from this group. `ongoing_care` in particular remains
 * live in the type even though round 7 also removed the Care Coordination
 * *column* — between the two changes it's neither visible nor selectable
 * anywhere in the roster, and recency lives in the Last Engaged column
 * instead. It used to also gate the Outcome Goal filter (`hasMeasurableOutcome`
 * in filters.ts), which made "Worsening" return only ongoing-care members with
 * nothing on screen saying so — round 8 removed that filter entirely, so the
 * interaction is moot rather than merely confirmed-as-intended now.
 */
export const CARE_COORDINATION_FILTER_STATUSES: CareCoordinationStatus[] = [
  'never_logged_in',
  'onboarding',
  'baseline_complete',
  'booked_initial_visit',
  'initial_visit_occurred',
];

/**
 * Care Coordination filter options. **Single-select** as of round 6 — a member
 * occupies exactly one funnel stage, so the multi-select this used to be only
 * ever expressed an OR across stages, and the team asked for one stage at a
 * time. See `matchesCareCoordination`.
 */
export const CARE_COORDINATION_OPTIONS: FilterOption<CareCoordinationStatus>[] =
  CARE_COORDINATION_FILTER_STATUSES.map((value) => ({
    value,
    label: CARE_COORDINATION_LABELS[value],
  }));

/**
 * How many days back "recently signed up" looks. Shared by the filter
 * (`isRecentSignup` in `filters.ts`) and the panel's "New This Week" stat, so
 * the two can't quietly drift apart.
 */
export const NEW_SIGNUP_WINDOW_DAYS = 7;

/**
 * Enrollment Status — where a member sits relative to the program itself,
 * as opposed to where they sit in the care funnel.
 *
 * New in round 6, and assembled from two values that used to live elsewhere:
 * "New" was a sentinel pill inside Care Coordination, and Cancelled was one of
 * that group's funnel stages. Neither belonged there. `new` is signup recency,
 * which co-occurs with any funnel stage; `cancelled` means the member has left
 * the funnel entirely. Pulling both out is what let Care Coordination become a
 * clean single-select (see `CARE_COORDINATION_FILTER_STATUSES`).
 *
 * Single-select per the team's framework. Note the two values aren't strictly
 * mutually exclusive in the data — somebody could sign up and cancel inside
 * the same week — so this is a deliberate product simplification, not a
 * property of the model.
 */
export const ENROLLMENT_NEW = 'new';
export const ENROLLMENT_CANCELLED = 'cancelled';

export type EnrollmentStatus =
  | typeof ENROLLMENT_NEW
  | typeof ENROLLMENT_CANCELLED;

export const ENROLLMENT_STATUS_OPTIONS: FilterOption<EnrollmentStatus>[] = [
  { value: ENROLLMENT_NEW, label: `New (last ${NEW_SIGNUP_WINDOW_DAYS} days)` },
  { value: ENROLLMENT_CANCELLED, label: 'Cancelled' },
];

/**
 * Risk stratification. `n/a` is a filter-only value — it selects members with
 * no rating yet (`risk_stratification` null/undefined), which is a real thing
 * to want to see (nobody has assessed them) rather than a missing-data gap.
 */
export const RISK_FILTER_NA = 'n/a';

export const RISK_OPTIONS: FilterOption<
  NonNullable<RiskLevel> | typeof RISK_FILTER_NA
>[] = [
  { value: 'high', label: 'High' },
  { value: 'moderate', label: 'Medium' },
  { value: 'low', label: 'Low' },
  { value: RISK_FILTER_NA, label: 'N/A' },
];

/**
 * Appointment timing. Single-select — the buckets overlap, so combining is
 * meaningless, and it's also what keeps a past window and a future window from
 * ever being active at once (round 7). No disabled states are needed for that
 * rule: the control can't express the combination in the first place.
 *
 * `last_week`/`last_month` look backwards; everything else looks forwards.
 * Rolling windows throughout, not calendar boundaries — see
 * `matchesVisitStatus`.
 */
export type VisitTiming =
  | 'today'
  | 'this_week'
  | 'this_month'
  | 'last_week'
  | 'last_month'
  | 'none_scheduled';

/** The backward-looking buckets. A member matches on an appointment that already happened. */
export const PAST_VISIT_TIMINGS: VisitTiming[] = ['last_week', 'last_month'];

/**
 * Whether a timing selection is asking about the past.
 *
 * Drives three things that all have to agree: which appointments the row
 * displays at all (past appointments are hidden unless explicitly asked for),
 * which direction the list sorts, and what the appointment column's header
 * says. One predicate so they can't drift.
 */
export const isPastTiming = (timing: VisitTiming | null): boolean =>
  timing !== null && PAST_VISIT_TIMINGS.includes(timing);

/**
 * Rendered as two labelled rows rather than one undifferentiated strip (see
 * `AppointmentsSection`) — "when is their next appointment" and "when was
 * their last one" are different questions, and the grouping is what tells a
 * user they're mutually exclusive. Both rows drive this one value.
 *
 * `none_scheduled` sits with the upcoming options rather than on a row of its
 * own: it is an answer about the upcoming window ("upcoming: none"), so it
 * belongs beside the windows it's the absence of. On its own row it read as a
 * third category, which it isn't.
 */
export const UPCOMING_VISIT_TIMING_OPTIONS: FilterOption<VisitTiming>[] = [
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'Next 7 Days' },
  { value: 'this_month', label: 'Next 30 Days' },
  { value: 'none_scheduled', label: 'None Scheduled' },
];

export const PAST_VISIT_TIMING_OPTIONS: FilterOption<VisitTiming>[] = [
  { value: 'last_week', label: 'Last 7 Days' },
  { value: 'last_month', label: 'Last 30 Days' },
];

export const VISIT_TIMING_OPTIONS: FilterOption<VisitTiming>[] = [
  ...UPCOMING_VISIT_TIMING_OPTIONS,
  ...PAST_VISIT_TIMING_OPTIONS,
];

export type VisitConfirmation = 'confirmed' | 'unconfirmed';

export const VISIT_CONFIRMATION_OPTIONS: FilterOption<VisitConfirmation>[] = [
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'unconfirmed', label: 'Unconfirmed' },
];

export const CONDITION_OPTIONS: FilterOption<ConditionCategory>[] = [
  { value: 'gi', label: 'GI' },
  { value: 'autoimmune', label: 'Autoimmune' },
  { value: 'weight_management', label: 'Weight Management' },
];

/**
 * User types the roster filters on.
 *
 * A subset of the backend enum above. The rest — beta user, payment failed,
 * free trial did not convert — are billing and lifecycle states rather than
 * populations anyone works a patient list against, and listing them made the
 * control look like it offered six meaningful choices when it offered three.
 * True users first: it's the default.
 */
export const USER_TYPE_OPTIONS: FilterOption<string>[] = [
  { value: USER_TYPES.trueUser, label: USER_TYPES.trueUser },
  { value: USER_TYPES.internalUser, label: USER_TYPES.internalUser },
  { value: USER_TYPES.freeUser, label: USER_TYPES.freeUser },
];

/** The roster defaults to true users, per the brief. */
export const DEFAULT_USER_TYPES = [USER_TYPES.trueUser];

/**
 * Example "logged-in provider" the roster opens scoped to, per team feedback
 * (Aug 2026 round 3): the tab should default to True User + the current
 * provider's own panel. There's no real login system behind this prototype
 * — the mock authenticated user (`src/mocks/fixtures/provider.ts`) doesn't
 * correspond to anyone in the care-team-member list, so the auth-id match in
 * `useCareTeamMember` never actually finds a match. This is the fallback that
 * fires instead, standing in for real per-provider personalization until
 * there's an actual login to seed from.
 */
export const DEFAULT_EXAMPLE_PROVIDER = 'Dr. Maya Patel, MD';
